
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { BarChart3, Loader2 } from 'lucide-react';
import { useOpenAIService } from '@/hooks/useOpenAIService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface DashboardSuggestion {
  metric: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  chartType: string;
}

export const DashboardSuggestionsAI = () => {
  const [industry, setIndustry] = useState('');
  const [suggestions, setSuggestions] = useState<DashboardSuggestion[]>([]);
  const { callAIService, isLoading } = useOpenAIService();

  const handleGenerateSuggestions = async () => {
    if (!industry) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('Please sign in to get AI suggestions');
        return;
      }

      // Get user's actual data from connected sources
      const { data: fileData } = await supabase
        .from('file_storage')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_folder', false)
        .limit(10);

      // Analyze actual user data to generate contextual suggestions
      let actualDataAnalysis: any = {};
      let dataColumns: string[] = [];
      
      if (fileData && fileData.length > 0) {
        // Try to analyze the first file to understand data structure
        try {
          const firstFile = fileData[0];
          const { data: fileContent, error } = await supabase.storage
            .from('user_files')
            .download(firstFile.file_path);
          
          if (!error && fileContent) {
            const text = await fileContent.text();
            if (firstFile.mime_type?.includes('csv') || firstFile.file_name.endsWith('.csv')) {
              const lines = text.split('\n').filter(line => line.trim());
              if (lines.length > 1) {
                const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
                dataColumns = headers;
                
                // Analyze sample data
                const sampleRow = lines[1]?.split(',').map(v => v.trim().replace(/"/g, '')) || [];
                actualDataAnalysis = {
                  columns: headers,
                  sample_data: Object.fromEntries(headers.map((h, i) => [h, sampleRow[i] || ''])),
                  data_types: headers.map(header => {
                    const values = lines.slice(1, 6).map(line => {
                      const vals = line.split(',');
                      return vals[headers.indexOf(header)]?.trim().replace(/"/g, '') || '';
                    }).filter(v => v);
                    
                    const isNumeric = values.every(v => !isNaN(Number(v)) && isFinite(Number(v)));
                    return { column: header, type: isNumeric ? 'number' : 'string' };
                  })
                };
              }
            }
          }
        } catch (err) {
          console.error('Error analyzing file:', err);
        }
      }

      const enhancedDataStructure = {
        files_available: fileData?.length || 0,
        user_industry: industry,
        actual_data_analysis: actualDataAnalysis,
        data_columns: dataColumns,
        suggested_charts: industry === 'finance' ? 
          ['Revenue Trend (Line)', 'Expense Breakdown (Pie)', 'Profit Margins (Bar)', 'Cash Flow (Area)'] :
          industry === 'ecommerce' ? 
          ['Sales Performance (Line)', 'Product Categories (Pie)', 'Conversion Rates (Bar)', 'Customer Segments (Donut)'] :
          industry === 'logistics' ? 
          ['Delivery Times (Line)', 'Route Efficiency (Bar)', 'Cost Analysis (Pie)', 'Performance Metrics (Area)'] :
          ['Performance Trends (Line)', 'Category Distribution (Pie)', 'Comparative Analysis (Bar)', 'Growth Metrics (Area)']
      };

      const aiResponse = await callAIService({
        feature: 'dashboard_suggestions',
        data: enhancedDataStructure,
        industry,
        userId: user.id,
        interactionType: 'dashboard_suggestions'
      });

      // Create intelligent suggestions based on actual data and AI insights
      const baseChartTypes = ['line', 'bar', 'pie'];
      const intelligentSuggestions: DashboardSuggestion[] = [];

      // Generate suggestions based on detected data columns
      if (dataColumns.length > 0) {
        const numericColumns = actualDataAnalysis.data_types?.filter((dt: any) => dt.type === 'number') || [];
        const stringColumns = actualDataAnalysis.data_types?.filter((dt: any) => dt.type === 'string') || [];

        // Time series suggestions if date-like columns exist
        const dateColumns = dataColumns.filter(col => 
          col.toLowerCase().includes('date') || 
          col.toLowerCase().includes('time') || 
          col.toLowerCase().includes('month') ||
          col.toLowerCase().includes('year')
        );

        if (dateColumns.length > 0 && numericColumns.length > 0) {
          intelligentSuggestions.push({
            metric: `${numericColumns[0].column} Trend Over Time`,
            description: `Track ${numericColumns[0].column} changes over ${dateColumns[0]} for trend analysis`,
            priority: 'high',
            chartType: 'line'
          });
        }

        // Category analysis for string columns
        if (stringColumns.length > 0) {
          intelligentSuggestions.push({
            metric: `Distribution by ${stringColumns[0].column}`,
            description: `Analyze data distribution across different ${stringColumns[0].column} categories`,
            priority: 'medium',
            chartType: 'pie'
          });
        }

        // Numeric comparisons
        if (numericColumns.length >= 2) {
          intelligentSuggestions.push({
            metric: `${numericColumns[0].column} vs ${numericColumns[1].column}`,
            description: `Compare ${numericColumns[0].column} and ${numericColumns[1].column} for correlation insights`,
            priority: 'medium',
            chartType: 'bar'
          });
        }
      }

      // Fallback to industry-specific suggestions if no data analysis possible
      if (intelligentSuggestions.length === 0) {
        const fallbackSuggestions = [
          {
            metric: industry === 'finance' ? 'Revenue Trends' : 
                    industry === 'ecommerce' ? 'Sales Conversion' : 
                    industry === 'logistics' ? 'Delivery Performance' : 'Key Performance',
            description: `Primary ${industry} metric for tracking business performance and growth`,
            priority: 'high' as const,
            chartType: 'line' as const
          },
          {
            metric: industry === 'finance' ? 'Risk Analysis' : 
                    industry === 'ecommerce' ? 'Customer Behavior' : 
                    industry === 'logistics' ? 'Route Efficiency' : 'Operational Metrics',
            description: `Critical ${industry} insights for strategic decision making`,
            priority: 'medium' as const,
            chartType: 'bar' as const
          },
          {
            metric: industry === 'finance' ? 'Portfolio Balance' : 
                    industry === 'ecommerce' ? 'Product Performance' : 
                    industry === 'logistics' ? 'Cost Distribution' : 'Resource Allocation',
            description: `Detailed breakdown of ${industry} components and distribution`,
            priority: 'medium' as const,
            chartType: 'pie' as const
          }
        ];
        intelligentSuggestions.push(...fallbackSuggestions);
      }

      setSuggestions(intelligentSuggestions);
      toast.success(`Generated ${intelligentSuggestions.length} AI-powered dashboard suggestions based on your data!`);
    } catch (error) {
      console.error('Failed to generate dashboard suggestions:', error);
      toast.error('Failed to generate suggestions. Please try again.');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">AI Dashboard Suggestions</h3>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Industry</label>
          <Select value={industry} onValueChange={setIndustry}>
            <SelectTrigger>
              <SelectValue placeholder="Select industry for dashboard suggestions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="finance">Finance</SelectItem>
              <SelectItem value="ecommerce">E-commerce</SelectItem>
              <SelectItem value="logistics">Logistics</SelectItem>
              <SelectItem value="realestate">Real Estate</SelectItem>
              <SelectItem value="technology">Technology</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button 
          onClick={handleGenerateSuggestions}
          disabled={isLoading || !industry}
          className="w-full"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generating Suggestions...
            </>
          ) : (
            'Get AI Dashboard Suggestions'
          )}
        </Button>

        {suggestions.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium">Recommended Metrics:</h4>
            {suggestions.map((suggestion, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-medium">{suggestion.metric}</h5>
                  <Badge className={getPriorityColor(suggestion.priority)}>
                    {suggestion.priority}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-2">{suggestion.description}</p>
                <div className="text-xs text-muted-foreground">
                  Recommended chart: {suggestion.chartType}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};
